import './previewPinVerifyPage.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { red } from '@mui/material/colors';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import uniqueId from 'lodash/uniqueId';
import {
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../../assets/vehicle-suv-sideview-two.png';
import { Header } from '../../../components/Header/Header';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { resetStore } from '../../../store/_core/resetStore';
import useApiStore from '../../../store/apiStore/useApiStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useParameterStore from '../../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../../store/pinStore/usePinStore';
import usePreviewStore from '../../../store/previewStore/usePreviewStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const { applicationType, leadChannelCode, vehicle_vin } = useParameterStore();
  const { hydrate } = useApplyStore();
  const { deliverPin, deliverPinRetrySuccess, deliverPinFailedMessage } =
    usePinStore();
  const { firstName, lastName, primaryPhoneNumber, primaryEmailAddress } =
    usePreviewStore();
  const {
    clearPinPurpose,
    clearPinRetryState,
    setUserFirstName,
    setUserLastName,
    setUserSignedUp,
    userAuthenticate,
    userCreate,
    userUpdate,
    userCreateFailedMessage,
    userAuthenticateFailedMessage,
    computed: { shouldCreateUser, isPinPurposeEmpty },
    pinPurpose,
    getDeliverPinParams,
  } = useUserStore();
  const { register, handleSubmit, reset, getValues, setValue } = useForm();

  const amountOfInputs = 6;
  const InputsList = [...Array(amountOfInputs).keys()];
  const inputRef = useRef<any>([]);
  const [focusedInputIndex, setFocusedInputIndex] = useState(0);

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber || isPinPurposeEmpty) {
      navigate('/sign-up');
    }

    startTimer();

    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber]);

  useEffect((): void => {
    if (userCreateFailedMessage || userAuthenticateFailedMessage) {
      reset();
    }
  }, [userCreateFailedMessage, userAuthenticateFailedMessage]);

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('PreviewPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('PreviewPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const pin = Object.values(values).join('');

    if (!pin) {
      return;
    }

    // Set before creating/authenticating so SessionGuard does not briefly redirect
    // the still-authenticating user away from this non-authenticated route.
    setUserSignedUp(true);

    if (shouldCreateUser) {
      await userCreate(
        firstName,
        lastName,
        primaryEmailAddress,
        primaryPhoneNumber,
        pin,
      );
    } else {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        pinPurpose,
      );

      setUserFirstName(firstName);
      setUserLastName(lastName);

      await userUpdate();
    }

    const { userCreateSuccess, userAuthenticateSuccess } =
      useUserStore.getState();

    if (userCreateSuccess || userAuthenticateSuccess) {
      const applyRoute = await hydrate({
        applicationType,
        source: HydrateSource.PREVIEW,
        vin: vehicle_vin,
      });

      navigateWithCallback(applyRoute).then((): void => {
        resetStore(usePreviewStore);
        clearPinPurpose();
      });
    }
  };

  const focusCurrentInput = (): void => {
    const formValues = Object.values(getValues()).join('');
    const numOfFilledInputs = formValues.length;

    const currentInput = inputRef.current[numOfFilledInputs];
    if (currentInput) {
      currentInput.focus();
    }
  };

  const determineInputFocus =
    (index: number) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      // Detect backspace to focus the previous input
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const previousInput = inputRef.current[index - 1];

        if (previousInput) {
          setValue(`verificationCode-${index - 1}`, '');

          previousInput.focus();
        }
      }

      // Detect current input value was not provided.
      // Needed to prevent focusing of next input due to
      // holding down of multiple keys at once
      const currentInput = inputRef.current[index];
      if (!currentInput.value) {
        return;
      }

      // Detect number was entered to focus the next input
      if (!isNaN(parseInt(event.key, 10))) {
        const nextInput = inputRef.current[index + 1];

        if (nextInput) {
          nextInput.focus();
        } else {
          currentInput.blur();

          // Submit form when last input value was provided
          handleSubmit(formSubmit)();
        }
      }
    };

  const splitOneTimeCode = (event: any): void => {
    const value = get(event, 'target.value');

    if (isNaN(parseInt(value, 10)) || value.length !== amountOfInputs) {
      return;
    }

    const chars: string[] = value.split('');

    chars.forEach((char, index): void => {
      setValue(`verificationCode-${index}`, char);
    });

    const lastInput = inputRef.current[inputRef.current.length - 1];
    if (lastInput) {
      lastInput.focus();
    }

    handleSubmit(formSubmit)();
  };

  const capturePasteEvent = (event: any): void => {
    // get clipboard data from paste event
    const clipboardData = get(event, 'clipboardData');

    if (clipboardData) {
      const value = clipboardData.getData('Text');

      // prevent paste if non-digits were provided
      if (!/^\d+$/g.test(value)) {
        event.preventDefault();
        return;
      }

      let nextInput;

      const maxCharsProvided = value.length >= amountOfInputs;

      if (value.length > 1) {
        // handle multiple digits being pasted
        // convert value to chars array, sliced to the max amount of inputs
        const chars: string[] = value.split('').slice(0, amountOfInputs);

        // assign the char value to each input
        chars.forEach((char, index): void => {
          setValue(`verificationCode-${index}`, char);
        });

        // determine the next input index
        const lastCharInputIndex = maxCharsProvided
          ? amountOfInputs - 1 // next input should be the last if max characters
          : value.length;

        nextInput = inputRef.current[lastCharInputIndex];
      } else if (value.length === 1) {
        // single digit was pasted, set value the currently focused input
        setValue(`verificationCode-${focusedInputIndex}`, value);

        // determine the next input
        nextInput = inputRef.current[focusedInputIndex + 1];
      }

      // prevent paste event from carrying over to the next input when focused
      event.preventDefault();

      // added as a fail safe but the next input to focus should always exist
      if (nextInput) {
        nextInput.focus();
      }

      // all chars were provided, submit the form
      if (maxCharsProvided) {
        handleSubmit(formSubmit)();
      }
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();

      clearPinRetryState();

      const deliverPinParams = getDeliverPinParams(
        DeliverPinLocation.RETRY,
        primaryPhoneNumber,
        primaryEmailAddress,
      );

      await deliverPin(deliverPinParams);

      inputRef.current[0].focus();
    }
  };

  return (
    <div className="preview-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {deliverPinRetrySuccess && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinRetrySuccess: false });
              }}
            >
              <Alert
                data-testid={locator.pages.previewPinVerify.alert.success}
                icon={<CheckCircleIcon />}
                severity="success"
              >
                {t('PreviewPinVerifyPage.codeSent')}
              </Alert>
            </Snackbar>
          )}

          {!isEmpty(deliverPinFailedMessage) && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinFailedMessage: null });
              }}
            >
              <Alert
                data-testid={locator.pages.previewPinVerify.alert.error}
                severity="error"
              >
                {deliverPinFailedMessage}
              </Alert>
            </Snackbar>
          )}

          <Header showLogo />

          <main>
            <figure>
              <img
                alt={t('imageAlts.vehicleSUVSideView')}
                src={vehicleSUVSideView}
              />
            </figure>
            <form noValidate onSubmit={handleSubmit(formSubmit)}>
              <div className="form-content">
                <h1 data-testid={locator.pages.previewPinVerify.form.h1}>
                  {t('PreviewPinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.previewPinVerify.form.h3}>
                  <Trans
                    components={[<u key={uniqueId('span-')} />]}
                    i18nKey={'PreviewPinVerifyPage.subtitle'}
                    values={{
                      phoneNumber:
                        inputEventHandler.phoneNumber.formatter(
                          primaryPhoneNumber,
                        ),
                    }}
                  />
                </h3>

                <div className="inputs-container" onClick={focusCurrentInput}>
                  {InputsList.map(
                    (index): ReactNode => (
                      <TextField
                        {...register(`verificationCode-${index}`)}
                        autoComplete={'one-time-code'}
                        autoFocus={index === 0}
                        disabled={isLoading}
                        error={
                          !isEmpty(userCreateFailedMessage) ||
                          !isEmpty(userAuthenticateFailedMessage)
                        }
                        inputRef={(element: HTMLInputElement): void => {
                          inputRef.current[index] = element;
                        }}
                        key={index}
                        slotProps={{
                          htmlInput: {
                            'data-testid':
                              locator.pages.previewPinVerify.form[
                                `pinInput${index + 1}`
                              ],
                            inputMode: 'numeric',
                          },
                        }}
                        style={{
                          pointerEvents:
                            focusedInputIndex !== index ? 'none' : 'auto',
                        }}
                        onChange={splitOneTimeCode}
                        onFocus={(): void => {
                          setFocusedInputIndex(index);
                        }}
                        onKeyDown={inputEventHandler.verificationCode.keyDown}
                        onKeyUp={determineInputFocus(index)}
                        onPasteCapture={capturePasteEvent}
                      />
                    ),
                  )}
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  {(!isEmpty(userCreateFailedMessage) ||
                    !isEmpty(userAuthenticateFailedMessage)) && (
                    <span
                      data-testid={
                        locator.pages.previewPinVerify.form.pinInputError
                      }
                      style={{
                        fontSize: '12px',
                        color: red[700],
                      }}
                    >
                      {userCreateFailedMessage || userAuthenticateFailedMessage}
                    </span>
                  )}
                </div>

                <Button
                  color="primary"
                  data-testid={
                    locator.pages.previewPinVerify.form.resendPinLink
                  }
                  disabled={seconds > 0}
                  loading={isLoading}
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </Button>
              </div>
              <footer data-testid={locator.pages.previewPinVerify.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'PreviewPinVerifyPage';
